import { createSelector } from 'reselect';
import { denormalize } from 'normalizr';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import {
  deviationSchema,
  deviationExtendedSchema,
} from '../normalizr/schemas/deviation';
import {
  getDataEntities,
  getDataEntityById,
  getDataEntitiesByIds,
} from '../entities';
import { NormalizedDeviation } from '../normalizr/types';
import { getUserById } from '../users/selectors';
import { getCurrentUser } from '../../publicSession/selectors';
import {
  getDeviationType,
  isDeviationAuthor,
} from '../../DeviationViews/_helpers';
import { DeviationTypes } from '../../types/deviation';
import DeviationMediaService from '../../utils/DeviationMediaService';
import { userSchema } from '../normalizr/schemas/user';

export const getNormalizedDeviationById = (
  state,
  entityId: string | number
): NormalizedDeviation | undefined =>
  getDataEntityById(state, deviationSchema, entityId);

const getNormalizedDeviationsByIds = (
  state,
  entityIds: (string | number)[]
): NormalizedDeviation[] =>
  getDataEntitiesByIds(state, deviationSchema, entityIds);

const getDeviationAuthorsByDeviationIds = (
  state,
  entityIds: (string | number)[]
): PapiUser[] => {
  const authorIds = getNormalizedDeviationsByIds(state, entityIds)
    .map(deviation => deviation.author!)
    .filter(authorId => !!authorId);
  return getDataEntitiesByIds(state, userSchema, authorIds);
};

const getDeviationAuthorByDeviationId = (
  state,
  entityId: string | number
): PapiUser | undefined => {
  const deviation = getNormalizedDeviationById(state, entityId);
  if (!deviation) {
    return;
  }

  return getUserById(state, deviation.author);
};

const getDeviationGroupDestinationsByDeviationIds = (
  state,
  entityIds: (string | number)[]
): PapiUser[] => {
  const groupDestinationIds = getNormalizedDeviationsByIds(state, entityIds)
    .map(deviation => deviation.groupDestination!)
    .filter(groupDestinationId => !!groupDestinationId);
  return getDataEntitiesByIds(state, userSchema, groupDestinationIds);
};

const getDeviationGroupDestinationByDeviationId = (
  state,
  entityId: string | number
): PapiUser | undefined => {
  const deviation = getNormalizedDeviationById(state, entityId);
  if (!deviation) {
    return;
  }

  return getUserById(state, deviation.groupDestination);
};

export const getHasCommentedOnDeviation = (
  state,
  deviation: PapiDeviation
): boolean | undefined => {
  if (!deviation.deviationId) {
    return false;
  }

  const normalizedDeviation = getNormalizedDeviationById(
    state,
    deviation.deviationId
  );

  return normalizedDeviation
    ? normalizedDeviation.hasCommentedOn
    : deviation.hasCommentedOn;
};

export const createGetDeviationById = () => {
  return createSelector(
    [
      getNormalizedDeviationById,
      getDeviationAuthorByDeviationId,
      getDeviationGroupDestinationByDeviationId,
    ],
    (deviation, author, groupDestination): PapiDeviation | undefined => {
      if (!deviation) {
        return undefined;
      }
      return {
        ...deviation,
        author,
        groupDestination,
        extended: undefined,
      };
    }
  );
};
// a default instance - prefer the selector creator for proper memoization
export const getDeviationById = createGetDeviationById();

export const getExtendedDeviationById = createSelector(
  [(state, entityId) => entityId, getDeviationById, getDataEntities],
  (entityId, deviation, entities): PapiDeviation | undefined => {
    if (!deviation || !entities[deviationExtendedSchema.key]) {
      return deviation;
    }

    // extended has a ton of stuff inside, needs to be denormalized
    deviation.extended = denormalize(
      entityId,
      deviationExtendedSchema,
      entities
    );
    return deviation;
  }
);

export const createGetDeviationsByIds = () => {
  return createSelector(
    getNormalizedDeviationsByIds,
    getDeviationAuthorsByDeviationIds,
    getDeviationGroupDestinationsByDeviationIds,
    (deviations, authors, groupDestinations): PapiDeviation[] => {
      return deviations.map(deviation => ({
        ...deviation,
        author: authors.find(author => author.userId === deviation.author),
        groupDestination: groupDestinations.find(
          groupDestination =>
            groupDestination.userId === deviation.groupDestination
        ),
        extended: undefined,
      }));
    }
  );
};
// a default instance - prefer the selector creator for proper memoization
export const getDeviationsByIds = createGetDeviationsByIds();

export const getIsGif = (deviation: PapiDeviation) =>
  DeviationMediaService.isGif(deviation);

export function getIsCurrentUserAuthorOf(state, deviation: PapiDeviation) {
  const user = getCurrentUser(state);
  return user ? isDeviationAuthor(deviation, user) : false;
}

export function getDeviationEditUrl({ extended, ...deviation }: PapiDeviation) {
  const deviationType = getDeviationType(deviation as PapiDeviation);

  // some types never go to edit page
  if (
    [
      DeviationTypes.COMMISSION,
      DeviationTypes.STATUS,
      DeviationTypes.POLL,
      DeviationTypes.TIER,
    ].includes(deviationType)
  ) {
    return;
  }

  if (deviation.legacyTextEditUrl) {
    return deviation.legacyTextEditUrl;
  }

  // non-legacy journals/lit don't go to edit page
  if (
    ![DeviationTypes.JOURNAL, DeviationTypes.LITERATURE].includes(deviationType)
  ) {
    return extended && extended.editUrl;
  }
}

export function getDeviationIdFromNormalizedId(
  state,
  normalizedId: string | number
) {
  const deviation = getNormalizedDeviationById(state, normalizedId);
  return (deviation && deviation.deviationId) || normalizedId;
}
